// Piezas comunes de las `acciones` del encabezado (2026-10-20): el formato del
// reloj y el texto del chip de rol. Lógica sin React, para que
// AppHeaderBase.jsx (RelojEncabezado, ChipRol) y la variante estática
// (app-header.js) muestren exactamente lo mismo. sync-ui.sh la copia junto con
// el encabezado (--only=header y --only=estatico). Solo anchoDeTexto y
// medirEncabezado (al final: cuándo entra el menú compacto) leen el DOM.
//
// Por qué: la revisión del 2026-10-20 encontró 24 relojes propios con 5
// formatos (con y sin segundos, "p.m.", fecha con o sin día) y chips de rol con
// textos en inglés ("admin", "viewer") y estilos distintos.
//
// Reloj: 24 h, hora de Costa Rica.
//   escritorio  'lun 20 oct · 17:24'
//   teléfono    '17:24'
// Rol: un solo texto por rol, en español.
//   admin → Administrador · editor → Editor · viewer/lectura → Lectura · kiosco → Kiosco
// Confirmar antes de volver al hub (2026-10-20): los textos del diálogo que
// abre el logo en los equipos compartidos (prop confirmarHub; ver README.md,
// 'Pantallas móviles y kioscos').

export const ZONA_CR = 'America/Costa_Rica';

const formatos = new Map();
function formato(zona) {
  if (!formatos.has(zona)) {
    formatos.set(zona, new Intl.DateTimeFormat('en-US', {
      timeZone: zona,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }));
  }
  return formatos.get(zona);
}

// Nombres propios y no los de Intl: cada navegador abrevia distinto ("sept",
// "sept.", "sep."; "mié" o "mié."), y el reloj tiene que decir y medir lo mismo
// en todas las apps. Intl solo da los números en la zona horaria.
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const dos = (n) => String(n).padStart(2, '0');

// { hora: '17:24', fecha: 'mar 20 oct', completo: 'mar 20 oct · 17:24', iso: '2026-10-20T17:24' }
export function formatoReloj(fecha = new Date(), zona = ZONA_CR) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  const p = {};
  for (const { type, value } of formato(zona).formatToParts(d)) p[type] = Number(value);
  const hora = `${dos(p.hour % 24)}:${dos(p.minute)}`;
  const diaSemana = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  const fechaCorta = `${DIAS[diaSemana]} ${p.day} ${MESES[p.month - 1]}`;
  return { hora, fecha: fechaCorta, completo: `${fechaCorta} · ${hora}`, iso: `${p.year}-${dos(p.month)}-${dos(p.day)}T${hora}` };
}

// Textos del chip de rol. Las claves son los roles que usan las apps del hub
// (app_role_of: admin / editor / viewer) y los nombres que algunas apps ya
// tenían (lectura, kiosco...). Un rol que no está acá sale con la primera letra
// en mayúscula (ej. 'supervisor' → 'Supervisor').
export const ETIQUETAS_ROL = {
  admin: 'Administrador',
  administrador: 'Administrador',
  administrator: 'Administrador',
  editor: 'Editor',
  viewer: 'Lectura',
  visor: 'Lectura',
  lectura: 'Lectura',
  lector: 'Lectura',
  read: 'Lectura',
  reader: 'Lectura',
  readonly: 'Lectura',
  solo_lectura: 'Lectura',
  kiosco: 'Kiosco',
  kiosko: 'Kiosco',
  kiosk: 'Kiosco',
};

export function etiquetaRol(rol) {
  const texto = String(rol ?? '').trim();
  if (!texto) return '';
  const clave = texto.toLowerCase().replace(/[\s-]+/g, '_');
  return ETIQUETAS_ROL[clave] || texto.charAt(0).toUpperCase() + texto.slice(1);
}

// Textos del diálogo de confirmarHub, iguales en AppHeaderBase.jsx y en
// app-header.js. Acepta un string (el mensaje) o { titulo, mensaje, confirmar,
// cancelar }; true usa todos los textos por defecto. Sin valor (false, '',
// null) devuelve null: el logo navega directo, como siempre.
export const TEXTOS_CONFIRMAR_HUB = {
  titulo: '¿Volver al hub?',
  mensaje: 'Va a salir de esta pantalla y abrir el hub de Vista Los Sueños.',
  confirmar: 'Ir al hub',
  cancelar: 'Quedarme',
};

export function textosConfirmarHub(confirmarHub) {
  if (!confirmarHub) return null;
  if (typeof confirmarHub === 'string') {
    return confirmarHub.trim() ? { ...TEXTOS_CONFIRMAR_HUB, mensaje: confirmarHub } : null;
  }
  if (typeof confirmarHub !== 'object') return { ...TEXTOS_CONFIRMAR_HUB };
  const texto = (v, porDefecto) => (typeof v === 'string' && v.trim() ? v : porDefecto);
  return {
    titulo: texto(confirmarHub.titulo, TEXTOS_CONFIRMAR_HUB.titulo),
    mensaje: texto(confirmarHub.mensaje, TEXTOS_CONFIRMAR_HUB.mensaje),
    confirmar: texto(confirmarHub.confirmar, TEXTOS_CONFIRMAR_HUB.confirmar),
    cancelar: texto(confirmarHub.cancelar, TEXTOS_CONFIRMAR_HUB.cancelar),
  };
}

// ─── Cuándo entra el menú compacto (2026-10-20, encabezado a todo el ancho) ───
// Pedido de Isaac: "que el appheader sea de toda pantalla para que se ocupe
// mejor el espacio". La fila ya no tiene max-w: va de borde a borde con relleno
// fijo (16 px en el teléfono, 24 px desde 640 y 32 px desde 1024), y el menú
// compacto ya no entra por un punto de corte fijo sino por el ancho REAL:
//   - bajo 640 px (teléfono): siempre menú compacto (regla de 390);
//   - desde 640 px: las secciones van en la fila si caben ENTERAS junto con el
//     título entero (o la ceja, la más ancha) y la derecha completa (acciones,
//     reloj, rol, iniciales y Salir); si no, todo al menú compacto.
// A 1920 px caben muchas más entradas que con el contenedor de 1024 de antes; a
// 768 px una app con 2 entradas ya puede ir en la fila. Sin medida (SSR, pruebas
// en jsdom, navegador sin ResizeObserver) manda el punto de corte de `navDesde`.
// modoEncabezado y necesarioFila son puros; medirEncabezado lee el DOM (sin
// React) y la usan AppHeaderBase.jsx y app-header.js por igual.
export const ANCHO_TELEFONO = 640;
export const GAP_FILA = 8; // gap de la fila (marca, secciones y derecha)
export const MARGEN_SECCIONES = 16; // las secciones van a 16 px del nombre
export const GAP_DERECHA = 4; // gap entre las piezas de la derecha
export const ANCHO_INICIALES = 36;
export const RELLENO_FILA = { telefono: 16, sm: 24, lg: 32 };

// Relleno lateral de la fila para un ancho de ventana (el mismo de las clases).
export function rellenoFila(anchoVentana) {
  if (anchoVentana >= 1024) return RELLENO_FILA.lg;
  if (anchoVentana >= ANCHO_TELEFONO) return RELLENO_FILA.sm;
  return RELLENO_FILA.telefono;
}

// Ancho que necesita la fila con las secciones adentro (px, sin el relleno).
//   marca     logo + separador y sus dos gaps (sin el nombre)
//   nombre    título entero o ceja, la más ancha
//   secciones las entradas de la fila con sus gaps
//   derecha   acciones, reloj, rol, iniciales y Salir (sin el botón de menú)
export function necesarioFila({ marca = 0, nombre = 0, secciones = 0, derecha = 0 } = {}) {
  const resto = derecha > 0 ? GAP_FILA + derecha : 0;
  return Math.ceil(marca + nombre + GAP_FILA + MARGEN_SECCIONES + secciones + resto);
}

// 'fila' | 'compacto'. `disponible` es el ancho interior de la fila (sin relleno).
export function modoEncabezado({ anchoVentana, disponible, ...medidas } = {}) {
  if (!(anchoVentana >= ANCHO_TELEFONO)) return 'compacto';
  return necesarioFila(medidas) <= disponible ? 'fila' : 'compacto';
}

let lienzo = null;
// Ancho natural de un texto con la fuente que el elemento tiene puesta (canvas;
// el título en la fila va cortado y su caja no dice cuánto mide entero).
export function anchoDeTexto(el, texto = el?.textContent || '') {
  const vista = el?.ownerDocument?.defaultView;
  if (!vista || !texto) return 0;
  const cs = vista.getComputedStyle(el);
  const t = cs.textTransform === 'uppercase' ? texto.toUpperCase() : texto;
  let ancho = 0;
  try {
    if (!lienzo || lienzo.ownerDocument !== el.ownerDocument) lienzo = el.ownerDocument.createElement('canvas');
    const ctx = typeof lienzo.getContext === 'function' ? lienzo.getContext('2d') : null;
    if (ctx) {
      ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      ancho = ctx.measureText(t).width;
    }
  } catch { /* sin canvas: estimación de abajo */ }
  const tam = parseFloat(cs.fontSize) || 16;
  if (!ancho) ancho = t.length * tam * 0.56;
  const espaciado = parseFloat(cs.letterSpacing);
  if (espaciado) ancho += espaciado * t.length;
  return Math.ceil(ancho);
}

// Mide el encabezado ya pintado y devuelve { modo, necesario, ...medidas }, o
// null si la fila no tiene ancho (sin layout: jsdom, SSR, encabezado oculto).
// Recibe los elementos: fila (la de 64 px), marca (bloque logo + nombre),
// nombreCaja (la columna ceja + título), titulo, ceja, secciones (el <nav>; en
// modo compacto queda con ancho 0 y overflow oculto, y su scrollWidth sigue
// siendo el ancho natural), derecha, toggle (botón de menú) e iniciales.
export function medirEncabezado({ fila, marca, nombreCaja, titulo, ceja, secciones, derecha, toggle, iniciales, usuario = false } = {}) {
  const vista = fila?.ownerDocument?.defaultView;
  if (!vista || !fila.clientWidth) return null;
  const cs = vista.getComputedStyle(fila);
  const disponible = fila.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
  const anchoVentana = vista.innerWidth || fila.ownerDocument.documentElement.clientWidth || 0;
  const medidas = {
    anchoVentana,
    disponible,
    marca: Math.max(0, (marca?.offsetWidth || 0) - (nombreCaja?.offsetWidth || 0)),
    nombre: Math.max(anchoDeTexto(titulo), ceja ? anchoDeTexto(ceja) : 0),
    secciones: secciones?.scrollWidth || 0,
    derecha: Math.max(0, (derecha?.offsetWidth || 0)
      - (toggle?.offsetWidth ? toggle.offsetWidth + GAP_DERECHA : 0)
      + (usuario && iniciales && !iniciales.offsetWidth ? ANCHO_INICIALES + GAP_DERECHA : 0)),
  };
  return { modo: modoEncabezado(medidas), necesario: necesarioFila(medidas), ...medidas };
}
